import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Editor } from '@monaco-editor/react';
import { ChevronRightIcon, DocumentDuplicateIcon, MinusIcon, PlusIcon, XMarkIcon, PlayIcon, InformationCircleIcon, HeartIcon, SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, SparklesIcon as SparklesIconSolid } from '@heroicons/react/24/solid';
import InlineFeedback from '../components/InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import { apiService } from '../services/api';


interface Section {
  id: string;
  title: string;
  description: string;
  sas_code: string;
  pyspark_code: string;
  isConverting: boolean;
  userConversionId?: string;  // If present, this is a user conversion (not default demo)
  isLiked: boolean;           // Track like status: false = neutral, true = liked
  aiEnabled: boolean;         // AI/LLM fallback enabled (default: true)
  error?: string | null;      // In-card error for convert/like failures
}

const ConverterDemo: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [sections, setSections] = useState<Section[]>([]);
  const [hoveredEditor, setHoveredEditor] = useState<string | null>(null);
  const [fontSize, setFontSize] = useState<number>(15);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactForm, setContactForm] = useState({ email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const contactFeedback = useFeedback(0);

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Per-editor copy status keyed by `${section.id}-left|right`
  const [copyStatus, setCopyStatus] = useState<{ key: string; status: 'copied' | 'error' } | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = (key: string, code: string) => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    navigator.clipboard.writeText(code)
      .then(() => setCopyStatus({ key, status: 'copied' }))
      .catch(() => setCopyStatus({ key, status: 'error' }))
      .finally(() => {
        copyTimerRef.current = setTimeout(() => setCopyStatus(null), 2000);
      });
  };

  const setSectionError = (sectionId: string, message: string | null) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, error: message } : s
    ));
  };

  const increaseFontSize = () => {
    setFontSize(prev => Math.min(prev + 1, 24));
  };

  const decreaseFontSize = () => {
    setFontSize(prev => Math.max(prev - 1, 10));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    contactFeedback.clear();

    try {
      await apiService.submitContactRequest({
        request_type: 'converter',
        email: contactForm.email,
        note: contactForm.note
      });
      setContactSubmitted(true);
      setContactForm({ email: '', note: '' });
    } catch (error) {
      contactFeedback.showError("Failed to send request. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const closeContactPopup = () => {
    setShowContactPopup(false);
    setContactForm({ email: '', note: '' });
    setContactSubmitted(false);
    contactFeedback.clear();
  };

  // Fetch demo sections using React Query
  const { data, isLoading, error } = useQuery({
    queryKey: ['converterDemo'],
    queryFn: () => apiService.getConverterDemo(),
  });

  // Initialize sections when data is loaded
  useEffect(() => {
    if (data?.sections && data.sections.length > 0) {
      // Initialize sections with isConverting, isLiked, and aiEnabled flags added
      const initialSections = data.sections.map((section: any) => ({
        ...section,
        isConverting: false,
        isLiked: false,  // Initialize as false (neutral) for default demo sections
        aiEnabled: true, // AI fallback enabled by default
      }));
      setSections(initialSections);
      setActiveSection(initialSections[0].id);
    }
  }, [data]);

  const handleLike = async (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section?.userConversionId) return;
    
    // Toggle between liked (true) and neutral (false)
    // false -> true (like it)
    // true -> false (neutral/remove like)
    const newLikeStatus = !section.isLiked;
    
    // Update UI optimistically
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, isLiked: newLikeStatus } : s
    ));
    
    try {
      await apiService.sendConverterFeedback(section.userConversionId, newLikeStatus);
    } catch (err) {
      // Revert on error
      setSections(prev => prev.map(s =>
        s.id === sectionId ? { ...s, isLiked: !newLikeStatus } : s
      ));
      setSectionError(sectionId, 'Failed to update feedback. Try again.');
    }
  };

  const toggleAI = (sectionId: string) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, aiEnabled: !s.aiEnabled } : s
    ));
  };

  const handleConvert = async (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    // Check if code is empty or only whitespace
    if (!section.sas_code.trim()) {
      setSectionError(sectionId, 'Enter some code to convert.');
      return;
    }

    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, isConverting: true, error: null } : s
    ));

    try {
      const response = await apiService.convertCode(section.sas_code, section.aiEnabled);
      
      setSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { 
              ...s, 
              pyspark_code: response.target_code, 
              isConverting: false,
              userConversionId: response.conversion_id,
              isLiked: false  // Initialize as false (neutral, no like yet)
            }
          : s
      ));
    } catch (err: any) {
      console.error('Conversion failed:', err);
      setSections(prev => prev.map(s => 
        s.id === sectionId 
          ? { 
              ...s, 
              pyspark_code: '', 
              isConverting: false,
              userConversionId: undefined,  // Clear on failure
              isLiked: false  // Reset to false (neutral) on failure
            } 
          : s
      ));
      
      // Extract error message from response
      const errorMessage = err.response?.data?.message || 'Failed to convert code. Try again.';

      // Handle rate limit error
      if (err.response?.status === 429) {
        setSectionError(sectionId, 'Rate limit reached. Try again later.');
      } else {
        setSectionError(sectionId, errorMessage);
      }
    }
  };

  const scrollToSection = (sectionId: string) => {
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleScroll = useCallback(() => {
    if (sections.length === 0) return;
    
    let closestSection = sections[0].id;
    let closestDistance = Infinity;
    
    // Find section whose top is closest to viewport top
    for (const section of sections) {
      const element = sectionRefs.current[section.id];
      if (element) {
        const rect = element.getBoundingClientRect();
        // Distance from section top to viewport top
        const distance = Math.abs(rect.top);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSection = section.id;
        }
      }
    }
    
    setActiveSection(closestSection);
  }, [sections]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent" role="status" aria-label="Loading converter demo"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-500 mb-4">Failed to load demo sections. Try again later.</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (sections.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-body-color">No demo sections available.</p>
      </div>
    );
  }

  return (
    <div className="flex gap-4 sm:gap-6 lg:gap-8">
      {/* Table of Contents - Left Sidebar */}
      <div className="w-64 flex-shrink-0 pl-4 sm:pl-6 lg:pl-8">
        <div className="sticky top-4">
          <h2 className="text-lg font-semibold text-heading mb-4">
            Contents
          </h2>
          <nav className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                  activeSection === section.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-body-color hover:bg-body-bg/50'
                }`}
              >
                <ChevronRightIcon 
                  className={`w-4 h-4 transition-transform flex-shrink-0 ${
                    activeSection === section.id ? 'rotate-90' : ''
                  }`}
                />
                <span className="text-sm line-clamp-2 break-words">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content - Natural Scrolling */}
      <div className="flex-1 pr-4 sm:pr-6 lg:pr-8">
        <div className="max-w-[100rem] mx-auto space-y-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-heading mb-2">
              Converter Showcase
            </h1>
            <p className="text-body-color mb-4">
              See how Alchemist Converter turns SAS code into PySpark.
            </p>
            
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-sm text-body-color flex items-start gap-2">
                <InformationCircleIcon className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>
                  <strong>Demo limits:</strong> This demo is for evaluation only. It limits input size and request rate. For production conversions,{' '}
                  <button 
                    onClick={() => setShowContactPopup(true)}
                    className="text-primary hover:text-primary-hover underline font-medium"
                  >
                    contact us
                  </button>.
                </span>
              </p>
            </div>
          </div>

          {/* Explanation text above examples */}
          <div className="mb-4">
            <p className="text-body-color">
              The examples below show how to convert single code snippets. In production, Alchemist Converter processes whole files and projects.
            </p>
          </div>

          {sections.map((section) => (
            <div
              key={section.id}
              ref={(el) => {
                sectionRefs.current[section.id] = el;
              }}
              data-section-id={section.id}
              className="bg-widget-bg rounded-lg border border-secondary p-6"
            >
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-heading">
                  {section.title}
                </h2>
                <button
                  onClick={() => toggleAI(section.id)}
                  className="p-1.5 rounded-md transition-colors hover:bg-body-bg/50"
                  title="AI fallback for code that rules alone cannot convert. Click to toggle."
                >
                  {section.aiEnabled ? (
                    <SparklesIconSolid className="w-5 h-5 text-primary" />
                  ) : (
                    <SparklesIcon className="w-5 h-5 text-body-color" />
                  )}
                </button>
              </div>
              <p className="text-body-color mb-4">{section.description}</p>

              {section.error && (
                <InlineFeedback feedback={{ kind: 'error', message: section.error }} className="mb-4" />
              )}

              {/* Code Editors Container */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* SAS Code Input */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 h-6">
                      <h3 className="text-sm font-medium text-heading">
                        SAS Code (Input)
                      </h3>
                      <button
                        onClick={() => handleConvert(section.id)}
                        disabled={section.isConverting}
                        className="btn-primary px-2 py-1"
                        title="Convert SAS to PySpark"
                      >
                        <PlayIcon className="w-3 h-3" />
                      </button>
                    </div>
                    <div 
                      className="overflow-visible relative"
                      onMouseEnter={() => setHoveredEditor(`${section.id}-left`)}
                      onMouseLeave={() => setHoveredEditor(null)}
                    >
                      {/* Editor controls */}
                      {hoveredEditor === `${section.id}-left` && (
                        <div className="absolute top-2 right-6 z-10 flex items-center gap-1">
                          <button
                            onClick={decreaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Decrease font size"
                          >
                            <MinusIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={increaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Increase font size"
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                          {section.sas_code && (
                            <button
                              onClick={() => handleCopy(`${section.id}-left`, section.sas_code)}
                              className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                              title={copyStatus?.key === `${section.id}-left`
                                ? (copyStatus.status === 'copied' ? 'Copied!' : 'Failed to copy code')
                                : 'Copy code'}
                            >
                              {copyStatus?.key === `${section.id}-left` && copyStatus.status === 'copied' ? (
                                <CheckIcon className="w-4 h-4 text-primary" />
                              ) : copyStatus?.key === `${section.id}-left` && copyStatus.status === 'error' ? (
                                <XMarkIcon className="w-4 h-4 text-red-400" />
                              ) : (
                                <DocumentDuplicateIcon className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                      <Editor
                        height="600px"
                        defaultLanguage="sas"
                        value={section.sas_code}
                        onChange={(value) => {
                          setSections(prev => prev.map(s => 
                            s.id === section.id ? { ...s, sas_code: value || '' } : s
                          ));
                        }}
                        theme="vs-dark"
                        options={{
                          minimap: { enabled: false },
                          fontSize: fontSize,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          wordWrap: 'on',
                          wrappingStrategy: 'advanced',
                          automaticLayout: true,
                          padding: { top: 10, bottom: 10 },
                          contextmenu: false,
                          fixedOverflowWidgets: false,
                          scrollbar: {
                            vertical: 'visible',
                            alwaysConsumeMouseWheel: false
                          },
                        }}
                        onMount={(editor, monaco) => {
                          // Disable F1 command palette
                          editor.addCommand(monaco.KeyCode.F1, () => {
                            // Do nothing - prevents command palette from opening
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* PySpark Code Output */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between h-6">
                      <h3 className="text-sm font-medium text-heading">
                        PySpark Code (Output)
                      </h3>
                      {/* Like button - only show for user conversions with successful results */}
                      {section.userConversionId && (
                        <button
                          onClick={() => handleLike(section.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors hover:bg-body-bg/50"
                          title={section.isLiked ? "Remove like" : "Like this conversion"}
                        >
                          {section.isLiked ? (
                            <HeartIconSolid className="w-4 h-4 text-red-500" />
                          ) : (
                            <HeartIcon className="w-4 h-4 text-body-color hover:text-red-500" />
                          )}
                          <span className="text-body-color">
                            {section.isLiked ? 'Liked' : 'Like'}
                          </span>
                        </button>
                      )}
                    </div>
                    <div 
                      className="overflow-visible relative"
                      onMouseEnter={() => setHoveredEditor(`${section.id}-right`)}
                      onMouseLeave={() => setHoveredEditor(null)}
                    >
                      {/* Editor controls - show font size buttons always, copy only if there's code */}
                      {hoveredEditor === `${section.id}-right` && (
                        <div className="absolute top-2 right-6 z-20 flex items-center gap-1">
                          <button
                            onClick={decreaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Decrease font size"
                          >
                            <MinusIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={increaseFontSize}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                            title="Increase font size"
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                          {section.pyspark_code && (
                            <button
                              onClick={() => handleCopy(`${section.id}-right`, section.pyspark_code)}
                              className="p-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-all opacity-90 hover:opacity-100"
                              title={copyStatus?.key === `${section.id}-right`
                                ? (copyStatus.status === 'copied' ? 'Copied!' : 'Failed to copy code')
                                : 'Copy code'}
                            >
                              {copyStatus?.key === `${section.id}-right` && copyStatus.status === 'copied' ? (
                                <CheckIcon className="w-4 h-4 text-primary" />
                              ) : copyStatus?.key === `${section.id}-right` && copyStatus.status === 'error' ? (
                                <XMarkIcon className="w-4 h-4 text-red-400" />
                              ) : (
                                <DocumentDuplicateIcon className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                      {section.isConverting && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-md">
                          <div className="bg-widget-bg rounded-lg px-6 py-4 flex items-center gap-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
                            <span className="text-body-color">Converting...</span>
                          </div>
                        </div>
                      )}
                      <Editor
                        height="600px"
                        defaultLanguage="python"
                        value={section.pyspark_code}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: fontSize,
                          lineNumbers: 'on',
                          scrollBeyondLastLine: false,
                          wordWrap: 'on',
                          wrappingStrategy: 'advanced',
                          automaticLayout: true,
                          padding: { top: 10, bottom: 10 },
                          contextmenu: false,
                          fixedOverflowWidgets: false,
                          scrollbar: {
                            vertical: 'visible',
                            alwaysConsumeMouseWheel: false
                          },
                        }}
                        onMount={(editor, monaco) => {
                          // Disable F1 command palette
                          editor.addCommand(monaco.KeyCode.F1, () => {
                            // Do nothing - prevents command palette from opening
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Contact Us Popup - Same as Layout component */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-heading">
                Contact Us for Converter
              </h2>
              <button
                onClick={closeContactPopup}
                className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-body-color mb-6">
              The Alchemist Converter is an enterprise feature. It converts your legacy SAS code to modern platforms like Python and Spark.
            </p>
            {contactSubmitted ? (
              <div className="space-y-4">
                <InlineFeedback
                  feedback={{ kind: 'success', message: 'We got your request. We will contact you soon.' }}
                />
                <button onClick={closeContactPopup} className="w-full btn-primary-full py-3">
                  Done
                </button>
              </div>
            ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-secondary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="note" className="block text-sm text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="note"
                  rows={4}
                  required
                  value={contactForm.note}
                  onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder="Tell us about your project and requirements..."
                />
              </div>
              <InlineFeedback feedback={contactFeedback.feedback} />
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-primary-full py-3"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  onClick={closeContactPopup}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConverterDemo;
